import type { UserLocalStorage } from '../../types/localStorage';
import type { AdditionalDataFormType } from './AdditionalDataModalContent';

import { useCallback } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import {
  declineAdditionalUserData,
  putAdditionalUserData,
} from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';

import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';

import AdditionalDataModalContent from './AdditionalDataModalContent';

export const useAdditionalDataModal = (testId: string) => {
  const modal = useModals();
  const jwt = useToken();
  const { t } = useTranslation();
  const [user, setUser] = useLocalStorageState<UserLocalStorage>('cms.user');

  const handlePutAdditionalDataModalContent = useCallback(
    async (payload: AdditionalDataFormType) => {
      if (!user) return undefined;

      try {
        const { body, status, ok } = await putAdditionalUserData(
          jwt,
          undefined,
          {
            id: user.data.id,
            ...payload,
          },
        );
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        setUser({
          ...user,
          data: {
            ...user.data,
            additionalInformationStatus: 'done',
            workType: payload.workType,
          },
        });

        TagManager.dataLayer({
          dataLayer: {
            event: 'user_info_form_submit',
            ...payload,
          },
        });

        toast.success(t('AdditionalDataModal.Form.SubmitSuccess'));
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(t('Form.TryAgain'));
        }
      }
    },
    [jwt, setUser, t, user],
  );

  const handleDeclineAdditionalDataButton = useCallback(async () => {
    if (!user) return undefined;

    try {
      const { body, status, ok } = await declineAdditionalUserData(
        jwt,
        undefined,
        {
          id: user.data?.id,
        },
      );
      if (!ok) {
        throw parseResponseErrors(body, status);
      }

      if (body.additionalInformationModalOpenAt) {
        setUser({
          ...user,
          data: {
            ...user.data,
            additionalInformationModalOpenAt: {
              date: body.additionalInformationModalOpenAt,
            },
          },
        });
      }

      toast.success(t('AdditionalDataModal.Form.FormDeclinedSuccess'));
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
      } else {
        toast.error(t('AdditionalDataModal.Form.FormDeclinedError'));
      }
    }
  }, [jwt, setUser, t, user]);

  const openAdditionalDataModal = useCallback(
    async (isOptional: boolean) => {
      if (!user) return undefined;
      const result = await modal({
        id: 'additional-data-modal-id',
        content: (
          <AdditionalDataModalContent
            testId={testId}
            handlePutAdditionalDataModalData={
              handlePutAdditionalDataModalContent
            }
            handleDeclineAdditionalDataModal={handleDeclineAdditionalDataButton}
            existingUser={isOptional}
          />
        ),
        contentAdditionalClasses: 'px-6 md:px-11 pb-20 md:overflow-visible',
        dialogAdditionalClasses: 'md:overflow-visible',
        size: 'xl',
        hideClose: !isOptional,
      });

      if (result == null) {
        await handleDeclineAdditionalDataButton();
      }
    },
    [
      handleDeclineAdditionalDataButton,
      handlePutAdditionalDataModalContent,
      modal,
      testId,
      user,
    ],
  );

  return { openAdditionalDataModal };
};
